
import { useState, useRef, useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom'

//Components
import { Grid } from "@mui/material";
import {
    Modal,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    Form,
    RadioButton,
    ProgressBar,
} from "@gpstrackit/react-controls";
import { DateRangePicker, DatePicker } from "@gpstrackit/gtc-react-controls";
import CustomCard from 'Components/Card';
import CustomSelect from 'Components/Select';
import moment from 'moment';
import { copyToClipboard } from 'Core/data/Helpers';
import Alert from '@mui/material/Alert';

//Notification
import { NotificationManager } from "Components/Notifications";

//Styles
import './reprocessOdoHours.scss';

//Actions
import {
    reprocessOdoHoursGetDeviceRequest,
    reprocessOdoHoursSetDevice,
    reprocessOdoHoursRequest,
    clearRedux,
} from 'Redux/actions';

import { getAccounts } from './redux/saga'

const ReprocessOdoHours = (props) => {

    const { messages } = useIntl();
    const dispatch = useDispatch();
    const navigate = useNavigate()
    const formRef = useRef(null);

    //Redux States
    const { device, loading, openModal, result, selectedDevices, selectedAccount } = useSelector(state => state.reprocessOdoHoursRedux)

    //Local States
    const [devices, setDevices] = useState([]);
    const [account, setAccount] = useState(selectedAccount);
    const [serial, setSerial] = useState("");
    const [step, setStep] = useState(1);
    const [processAll, setProcessAll] = useState(selectedDevices?.length > 0 ? false : true);
    const [startDate, setStartDate] = useState(moment.now());
    const [endDate, setEndDate] = useState(moment().add(2, 'hour'));
    const [highMessage, setHighMessage] = useState("");
    const [type, setType] = useState("Update");

    const searchDevice = () => {
        let deviceSearchCriteriaArr = serial.split(/[\s,;\t\n]+/);
        if (deviceSearchCriteriaArr.length > 1) {
            let criterias = [];

            deviceSearchCriteriaArr.forEach(criteria => {
                let escCriteria = criteria.trim();

                let regx = /[^a-zA-Z0-9]/g;
                escCriteria = escCriteria.replace(regx, '');

                if (devices && devices.length) {
                    for (let index in devices) {
                        let device = devices[index];

                        if (device.imei != escCriteria && device.esn != escCriteria) {
                            criterias.push(escCriteria?.trim());
                        }
                    }
                } else {
                    criterias.push(escCriteria?.trim());
                }
            });

            if (criterias.length) {
                dispatch(reprocessOdoHoursGetDeviceRequest(criterias, account.id));
            }

        } else {

            let isValid = true;
            for (let index in devices) {
                let device = devices[index];
                if (device.esn.toString() === serial?.trim()) {
                    isValid = false;
                    break;
                }
            }

            if (isValid) {
                dispatch(reprocessOdoHoursGetDeviceRequest(serial?.trim(), account.id));
            } else {
                NotificationManager.warning(
                    messages['reprocess_odometer_hours_device_is_already_added'],
                    messages['reprocess_odometer_hours_title'],
                    3000
                );
            }
        }
    }

    const closeModal = () => {
        setSerial("");
        setAccount(null);
        setDevices([]);
        dispatch(clearRedux("REPROCESS_ODOMETER_HOURS"));
    }

    const validateParameters = () => {

        let highDevices = false
        let highDateRange = false
        let devicesLength = processAll ? account.devicesCount : devices.length
        let months = moment(endDate).diff(moment(startDate), 'months')
        if (devicesLength > 300) {
            highDevices = true
        }
        if (months > 5) {
            highDateRange = true
        }

        if (highDevices && highDateRange) {
            setHighMessage(`${messages['reprocess_odometer_hours_high_init_message']} ${devicesLength} ${messages['reprocess_odometer_hours_devices']} ${messages['reprocess_odometer_hours_and']} ${months} ${messages['reprocess_odometer_hours_months']}`)
        } else if (highDevices) {
            setHighMessage(`${messages['reprocess_odometer_hours_high_init_message']} ${devicesLength} ${messages['reprocess_odometer_hours_devices_message']}`)
        } else if (highDateRange) {
            setHighMessage(`${messages['reprocess_odometer_hours_high_init_message']} ${months} ${messages['reprocess_odometer_hours_date_range_message']}`)
        }
    }

    const applyChanges = () => {

        let unitIds = devices.map((item) => (item?.id))
        let payload = {
            companyId: account?.id,
            unitIds,
            reprocessAll: processAll,
            startDate: `${moment(startDate).format("YYYY-MM-DD")}T00:00:00.000Z`,
            type
        }

        dispatch(reprocessOdoHoursRequest(payload))
    }

    const getAccountsForSelect = (input, callback) => {
        if (!input) {
            callback([])
            return
        }
        getAccounts(input).then((response) => {
            if (response.result) {
                const { items } = response?.result
                const accounts = items?.length ? items.filter(item => item?.id !== account?.id) : []
                callback(accounts)
            }
        })
    }

    useEffect(() => {
        if (device) {
            dispatch(clearRedux("REPROCESS_ODOMETER_HOURS", { device: null }));
            if (!device?.total) {
                NotificationManager.warning(
                    messages['reprocess_odometer_hours_device_has_not_been_found'],
                    messages['reprocess_odometer_hours_title'],
                    3000
                );
            } else {
                let items = device.items;

                if (items?.length > 0) {
                    setSerial("");
                    let newItems = [...devices, ...items]
                    dispatch(reprocessOdoHoursSetDevice(newItems))
                    setDevices([...newItems]);
                }
            }
        }
        if (selectedDevices && selectedDevices.length > 0) {
            let newDevices = selectedDevices.map((item) => ({ id: item?.id, imei: item?.imei, esn: item?.esn, deviceModel: item?.deviceModel, status: item?.status }));
            setDevices([...newDevices]);
            dispatch(clearRedux("REPROCESS_ODOMETER_HOURS", { selectedDevices: [] }))
        }
    }, [device, selectedDevices])

    useEffect(() => {

        if (result) {
            dispatch(clearRedux("REPROCESS_ODOMETER_HOURS", { result: null }))
            if (!result?.error) {
                let transactionId = result.result.transactionId;
                let message = messages["reprocess_odometer_hours_success_message"]
                NotificationManager.success(message, messages['reprocess_odometer_hours_title'], 10000, () => {
                    copyToClipboard(transactionId, messages['common_copied']);
                    navigate(`/app/transactions/list/${transactionId}`);

                }, null, '', null);
                closeModal();
                //getDevice && getDevice();
                //tableRef && tableRef?.current?.getData();
            } else {
                let message = messages['reprocess_odometer_hours_error_message']
                let msg = result?.error?.error
                if (msg?.includes("there are no records in fleet.CUnits")) {
                    message = messages['reprocess_odometer_hours_no_units_found']
                }else {
                    message = result?.error?.error
                }
                NotificationManager.error(
                    message,
                    messages['reprocess_odometer_hours_title'],
                    3000
                );

            }
        }
    }, [result])

    return (
        <Modal
            open={openModal}
            title={messages['reprocess_odometer_hours_title']}
            position="right"
            size="small"
            handleClose={closeModal}
            loading={loading}
        >
            <ModalBody>
                <ProgressBar steps={2} activeStep={step - 1} />
                {
                    step === 1 &&
                    <div style={{ padding: '0 10px', height: '78vh' }}>
                        <Grid
                            container
                            spacing={1}
                        >
                            <Grid item xs={12}>
                                <CustomSelect
                                    async
                                    value={account}
                                    onChange={(newaccount) => setAccount(newaccount)}
                                    label={messages['reprocess_odometer_hours_account']}
                                    loadOptions={getAccountsForSelect}
                                    required
                                    getOptionLabel={e => e.name}
                                    getOptionValue={e => e.id}
                                    className="account-select-reprocess-odo-hours"
                                />
                            </Grid>
                            <Grid item xs={12} style={{ marginTop: 10 }}>
                                <label className="input-normal-label" style={{ marginRight: 5 }}>{messages['reprocess_odometer_hours_type']}</label>
                            </Grid>
                            <Grid item xs={6} style={{ marginTop: 5 }} >
                                <RadioButton
                                    onChange={(value) => {
                                        setType("Update");
                                    }}
                                    value={type === "Update"}
                                    label={messages['reprocess_odometer_hours_type_update']}
                                    position="end"
                                />
                            </Grid>
                            <Grid item xs={6} style={{ marginTop: 5 }} >
                                <RadioButton
                                    onChange={(value) => {
                                        setType("Rebuild");
                                    }}
                                    value={type === "Rebuild"}
                                    label={messages['reprocess_odometer_hours_type_rebuild']}
                                    position="end"
                                />
                            </Grid>
                            <Grid item xs={12} style={{ marginTop: 10 }}>
                                <label className="input-normal-label" style={{ marginRight: 5 }}>{messages['reprocess_odometer_hours_devices_mode']}</label>
                            </Grid>
                            <Grid item xs={6} style={{ marginTop: 5 }} >
                                <RadioButton
                                    onChange={(value) => {
                                        setProcessAll(true);
                                    }}
                                    value={processAll}
                                    label={messages['reprocess_odometer_hours_all']}
                                    position="end"
                                />
                            </Grid>
                            <Grid item xs={6} style={{ marginTop: 5 }} >
                                <RadioButton
                                    onChange={(value) => {
                                        setProcessAll(false);
                                    }}
                                    value={!processAll}
                                    label={messages['reprocess_odometer_hours_specific']}
                                    position="end"
                                />
                            </Grid>
                            <Grid item xs={12} style={{ marginTop: 10 }} >
                                <label className="input-normal-label" style={{ marginRight: 5 }}>{messages['reprocess_odometer_hours_start_date']}</label>
                                <DatePicker
                                    calendarPosition="bottom"
                                    format="MMMM DD, yyyy"
                                    onChange={(date) => setStartDate(date)}
                                    placeholder={messages['feature_tab_select_date']}
                                    showButtons
                                    style={{ height: '42px', marginTop: 5 }}
                                    timeZone={0}
                                    value={startDate}
                                />
                                <div style={{ height: '100%', marginTop: 15 }}>
                                    <Input
                                        label={messages['reprocess_odometer_hours_end_date']}
                                        placeholder={messages['reprocess_odometer_hours_end_date']}
                                        value={moment(endDate).format("MMMM DD, yyyy h:mm a")}
                                        disabled
                                    />
                                </div>
                            </Grid>
                            {
                                !processAll &&
                                <Grid item xs={10}>
                                    <Form
                                        ref={formRef}
                                        onValidSubmit={() => searchDevice()}
                                    >
                                        <Input
                                            label={messages['reprocess_odometer_hours_input']}
                                            placeholder={messages['reprocess_odometer_hours_input']}
                                            value={serial}
                                            required
                                            requiredMessage={messages['required_field']}
                                            onChange={(value) => setSerial(value)}
                                        />
                                    </Form>
                                </Grid>
                            }
                            {
                                !processAll &&
                                <Grid item xs={2}>
                                    <div style={{ marginTop: 26 }}>
                                        <Button
                                            className="btn-dark-blue"
                                            onClick={() => formRef?.current?.submit()}
                                            disabled={!account}
                                        >
                                            {messages['common_add']}
                                        </Button>
                                    </div>
                                </Grid>
                            }
                        </Grid>
                        {
                            !processAll && devices?.length > 0 &&
                            <Grid item xs={12} style={{ marginTop: 26 }}>
                                <label style={{ fontWeight: 700 }}>{messages['reprocess_odometer_hours_devices']}</label>
                            </Grid>
                        }
                        {
                            !processAll && devices.length > 0 ? devices.map((item, index) => {
                                return (
                                    <CustomCard
                                        key={index}
                                        onClick={() => {
                                            let currentDevices = devices;
                                            let selected = currentDevices.findIndex((device) => device.id === item.id);
                                            if (selected > -1) currentDevices.splice(selected, 1);
                                            dispatch(reprocessOdoHoursSetDevice(currentDevices));
                                        }}
                                    >
                                        <div style={{ display: 'grid' }}>
                                            <div>
                                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['reprocess_odometer_hours_imei']}</label><span>{item.imei}</span>
                                            </div>
                                            <div>
                                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['reprocess_odometer_hours_serial']}</label><span>{item.esn}</span>
                                            </div>
                                            <div>
                                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['reprocess_odometer_hours_model']}</label><span>{item?.deviceModel?.name}</span>
                                            </div>
                                        </div>
                                    </CustomCard>
                                )
                            }) : <div style={{ display: 'flex', justifyContent: 'center', marginTop: 25 }} ><span style={{ opacity: 0.6 }} >{!processAll && messages['common_no_devices_added']}</span></div>}
                    </div>
                }
                {
                    step === 2 &&
                    <div style={{ padding: '0 10px', height: '78vh' }}>
                        <div style={{ display: 'grid' }}>
                            <div>
                                {
                                    highMessage !== "" &&
                                    <Alert severity="error">
                                        <span>{highMessage}</span>
                                    </Alert>
                                }
                            </div>
                            <div>
                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['reprocess_odometer_hours_account']}</label><span>{account?.name}</span>
                            </div>
                            <div>
                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['reprocess_odometer_hours_devices_to_reprocess']}</label><span>{processAll ? messages["reprocess_odometer_hours_all"] : devices.length}</span>
                            </div>
                            <div>
                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['reprocess_odometer_hours_selected_date']}</label><span>{`${moment(startDate).format("YYYY-MM-DD")} - ${moment(endDate).format("YYYY-MM-DD")}`}</span>
                            </div>
                            <div>
                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['reprocess_odometer_hours_message']}</label>
                            </div>
                        </div>
                    </div>
                }

            </ModalBody>
            <ModalFooter>
                {
                    <Button
                        className="btn-cancel"
                        onClick={() => closeModal()}
                    >
                        {messages['common_cancel']}
                    </Button>
                }
                {
                    step === 2 &&
                    <Button
                        className="btn btn-cancel btn-gray"
                        onClick={() => setStep(step - 1)}
                    >
                        {messages['common_back']}
                    </Button>
                }
                <Button
                    className="btn-dark-blue"
                    disabled={!account || (!processAll && devices?.length === 0)}
                    onClick={() => {
                        if (step === 2)
                            applyChanges()
                        else {
                            validateParameters()
                            setStep(step + 1)
                        }
                    }}
                >
                    {step === 2 ? messages['reprocess_odometer_hours_start_reprocess'] : messages['common_next']}
                </Button>
            </ModalFooter>
        </Modal >
    )
}

export default ReprocessOdoHours;